import { Link } from 'react-router-dom';
import { serviceAreas } from '../../../mocks/sharedData';

interface ServiceAreasProps {
  highlightCity?: string;
  customHeadline?: string;
  customSubtitle?: string;
  showMap?: boolean;
  mapQuery?: string;
}

export default function ServiceAreas({
  highlightCity,
  customHeadline = 'Nationwide Security Installation Coverage',
  customSubtitle = 'Professional security camera installers near you — serving homes and businesses across the United States.',
  showMap = false,
  mapQuery = 'New York, NY',
}: ServiceAreasProps) {
  const encodedMapQuery = encodeURIComponent(mapQuery);

  return (
    <section className="bg-white py-24 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-slate-100" />
      <div className="absolute bottom-0 left-0 w-full h-px bg-slate-100" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="inline-block px-4 py-1.5 rounded-full text-orange-700 text-xs font-bold uppercase tracking-[0.2em] border border-orange-200 bg-orange-50 mb-4">
            Service Coverage
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-black text-slate-900 mb-4">
            {customHeadline}
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto">{customSubtitle}</p>
        </div>

        {/* Capability badges */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {[
            'Security Camera Installers Near Me',
            'CCTV Installation Near Me',
            '48 States Covered',
            'Same-Day Service',
            '24/7 Support Line',
          ].map((badge) => (
            <span
              key={badge}
              className="px-4 py-2 rounded-full bg-slate-100 border border-slate-200 text-slate-700 text-sm font-semibold"
            >
              <i className="ri-checkbox-circle-fill text-orange-500 mr-1.5" />{badge}
            </span>
          ))}
        </div>

        {/* City pills */}
        <div className="flex flex-wrap justify-center gap-2 max-w-5xl mx-auto mb-12">
          {serviceAreas.map((area) => (
            <span
              key={area}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-200 cursor-default ${
                highlightCity && area.includes(highlightCity)
                  ? 'bg-orange-100 border-orange-400 text-orange-700'
                  : 'bg-slate-50 border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600'
              }`}
            >
              {area}
            </span>
          ))}
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl mx-auto mb-10">
          {[
            { value: '500+', label: 'Properties Secured', icon: 'ri-building-4-fill' },
            { value: '48', label: 'States Covered', icon: 'ri-map-fill' },
            { value: '24/7', label: 'Support Line', icon: 'ri-customer-service-fill' },
            { value: 'Same-Day', label: 'Install Available', icon: 'ri-timer-flash-fill' },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-slate-200 bg-slate-50 p-5 text-center">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-2">
                <i className={`${stat.icon} text-orange-500 text-lg`} />
              </div>
              <p className="text-slate-900 font-display font-black text-2xl">{stat.value}</p>
              <p className="text-slate-500 text-xs mt-1 font-medium">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Google Maps embed */}
        {showMap && (
          <div className="mb-10 rounded-2xl overflow-hidden border border-slate-200 relative">
            <div className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 shadow-sm">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-fill text-orange-500 text-sm" />
              </div>
              <span className="text-slate-700 text-xs font-semibold">{mapQuery} Service Area</span>
            </div>
            <iframe
              title={`${mapQuery} Service Area Map`}
              src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyD-9tSrke72PouQMnMX-a7eZSW0jkFMBWY&q=${encodedMapQuery}&zoom=11`}
              width="100%"
              height="420"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="w-full"
            />
          </div>
        )}

        <div className="text-center">
          <Link
            to="#contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-slate-900 text-white font-black text-sm hover:bg-slate-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Check If We Serve Your Area
            <i className="ri-map-pin-2-fill" />
          </Link>
        </div>
      </div>
    </section>
  );
}
